import {POLICY_CONSTANTS} from "@/constants/booking";

/**
 * Value sources for the shared policies content (policiesContent.jsx). Both
 * return the same raw shape so the content components don't care which surface
 * they render on:
 *  - bookingPolicyValues(): booking flow — /trips/calculate-policies via
 *    POLICY_CONSTANTS (cached; never fetches).
 *  - tripPolicyValues(policyPopup): trip pages — the GET-trip response's
 *    policy_popup.
 */

export function bookingPolicyValues() {
    return {
        waitingPickupFreeSeconds: POLICY_CONSTANTS.WAITING_PICKUP_FREE_SECONDS,
        waitingPickupRateCentsPerSecond: POLICY_CONSTANTS.WAITING_PICKUP_RATE_CENTS_PER_SECOND,
        waitingPickupMaxWaitSeconds: POLICY_CONSTANTS.WAITING_PICKUP_MAX_WAIT_SECONDS,
        freeCancellationHours: POLICY_CONSTANTS.FREE_CANCELLATION_HOURS,
        cancellationText: POLICY_CONSTANTS.CANCELLATION_TEXT,
        modificationText: POLICY_CONSTANTS.MODIFICATION_TEXT,
        // The no-show RULE (what happens to the fare), if calculate-policies carries one.
        // It is documented on /config/policies rather than here, so this is null today and
        // the popup falls back to matching the catalog — see utils/noShowPolicy.
        noShow: POLICY_CONSTANTS.NO_SHOW,
        loaded: POLICY_CONSTANTS.LOADED,
    };
}

// /trips/calculate-policies moved its waiting block from seconds to MINUTES on
// 2026-07-15, and POLICY_CONSTANTS converts back (see constants/booking.js). Whether
// the GET-trip `policy_popup` moved with it is UNCONFIRMED, so these read the minute
// key when the payload carries one and fall back to the legacy seconds key otherwise
// — correct under either shape, and self-healing if/when the trip payload switches.
// Both still return SECONDS, the contract policiesContent.jsx expects.
// Exported so RideConfirm — which builds its FAQ values straight off policy_popup
// rather than through tripPolicyValues — shares ONE definition of this fallback.
export const durationSeconds = (minutes, seconds) => (minutes != null ? minutes * 60 : (seconds ?? 0));
export const rateCentsPerSecond = (perMinute, perSecond) => (perMinute != null ? perMinute / 60 : (perSecond ?? 0));

export function tripPolicyValues(policyPopup, trip) {
    const pickup = policyPopup?.waiting?.pickup;
    return {
        // A trip's own no-show rule, already resolved to its direction and distance band —
        // the best source there is. `trip` is optional and only supplies the fallback
        // params the catalog is matched with when the payload has no rule of its own.
        noShow: policyPopup?.cancellation?.no_show ?? trip?.cancellation_policy?.no_show ?? null,
        noShowParams: trip ? {trip_direction: trip.trip_direction, distance_meters: trip.distance_meters} : null,
        waitingPickupFreeSeconds: durationSeconds(pickup?.free_minutes, pickup?.free_seconds),
        waitingPickupRateCentsPerSecond: rateCentsPerSecond(pickup?.rate_cents_per_minute, pickup?.rate_cents_per_second),
        waitingPickupMaxWaitSeconds: durationSeconds(pickup?.max_wait_minutes, pickup?.max_wait_seconds),
        freeCancellationHours: policyPopup?.cancellation?.free_cancellation_hours ?? 0,
        cancellationText: policyPopup?.cancellation?.text,
        modificationText: policyPopup?.modification?.text,
        loaded: !!policyPopup,
    };
}

/**
 * Values from the CONTEXT-FREE catalog (`/config/policies`) — for surfaces with no trip
 * at all, i.e. /help. Those used to hardcode the waiting figures because
 * /calculate-policies is trip-scoped and there is nothing to scope it to there.
 *
 * The catalog's waiting block is read through the same minutes-or-seconds fallback as
 * tripPolicyValues, so it is correct under either shape. `loaded` is false when the
 * block yields nothing (still fetching, or the keys sit somewhere else than assumed) —
 * callers treat that as "use my own defaults" rather than rendering a screen of zeros.
 *
 * @param {object|null} catalog
 */
export function catalogPolicyValues(catalog) {
    const pickup = catalog?.waiting?.pickup;
    const values = {
        waitingPickupFreeSeconds: durationSeconds(pickup?.free_minutes, pickup?.free_seconds),
        waitingPickupRateCentsPerSecond: rateCentsPerSecond(pickup?.rate_cents_per_minute, pickup?.rate_cents_per_second),
        waitingPickupMaxWaitSeconds: durationSeconds(pickup?.max_wait_minutes, pickup?.max_wait_seconds),
    };
    return {
        ...values,
        // The no-show rule is matched from the catalog by the popup itself (it needs a
        // direction/distance this surface doesn't have), so only the params ride along.
        noShow: null,
        noShowParams: null,
        loaded: values.waitingPickupFreeSeconds > 0 || values.waitingPickupMaxWaitSeconds > 0,
    };
}

// The `policy` prop shape CancellationModificationRefundContent expects
// (`cancellation.tiers` + `modification.tiers`). On a trip response the tiers do NOT
// live under `policy_popup` — the cancellation/refund tiers are in
// `cancellation_policy.tiers` and the modification tiers in `edit_policy.tiers`. This
// adapts a GET-trip object into that shape so the CMR popup shows the trip's real
// tiers instead of the "free" fallback.
//
// `direction` rides along because CMR's empty-modification-tiers row is from-airport
// only, and on a trip page there is no in-progress booking in sessionStorage to derive
// it from — TripResource carries `trip_direction` for exactly this.
export function tripCmrPolicy(trip) {
    return {
        cancellation: {tiers: trip?.cancellation_policy?.tiers ?? []},
        modification: {tiers: trip?.edit_policy?.tiers ?? []},
        direction: trip?.trip_direction ?? null,
    };
}
